import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { Breadcrumbs } from '@/components/common';
import { ChatWidget } from '@/components/support';
import { useAuth } from '@/context';
import styles from './Layout.module.css';

interface LayoutProps {
  title?: string;
  showSidebar?: boolean;
  showBreadcrumbs?: boolean;
}

export function Layout({ title, showSidebar = true, showBreadcrumbs = true }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Close sidebar when screen becomes larger
  useEffect(() => {
    function handleResize() {
      if (window.innerWidth > 768) {
        setSidebarOpen(false);
      }
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(prev => !prev);
  };

  return (
    <div className={styles.layout}>
      <Header title={title} onMenuToggle={showSidebar ? toggleSidebar : undefined} />
      <div className={styles.main}>
        {showSidebar && (
          <Sidebar
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />
        )}
        <main className={styles.content}>
          {showBreadcrumbs && <Breadcrumbs />}
          <Outlet />
        </main>
      </div>

      {/* Support Chat Widget - only render when authenticated */}
      {isAuthenticated && <ChatWidget />}
    </div>
  );
}
